// src/pages/AIPage.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { BsSend, BsSearch, BsLightbulb, BsList, BsArrowRight } from 'react-icons/bs';
import { Helmet } from 'react-helmet-async';
import ChatSidebar from '../components/ai/ChatSidebar';
import MessageBubble from '../components/ai/MessageBubble';
import WelcomeScreen from '../components/ai/WelcomeScreen';
import ThinkingSteps from '../components/ai/ThinkingSteps';
import { useSession } from '../hooks/useSession';
import { fetchSessions, fetchSessionsWithToken } from '../api/ai';   // ← import جدید

// Error Boundary
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    console.error('MessageBubble Error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="text-red-500 text-xs p-2 border border-red-300 rounded-lg bg-red-50">
          خطا در نمایش پیام. لطفاً صفحه را رفرش کنید.
        </div>
      );
    }
    return this.props.children;
  }
}

export default function AIPage() {
  const { isLoggedIn, openAuthModal, getAuthTokens } = useAuthContext();
  const navigate = useNavigate();

  const [chatStarted, setChatStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchWeb, setSearchWeb] = useState(false);
  const [deepSearch, setDeepSearch] = useState(true);
  const [thinkingSteps, setThinkingSteps] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const {
    sessionId,
    setSessionId,
    sessionFull,
    setSessionFull,
    startNewSession,
    loadSessionMessages,
    persistMessage,
  } = useSession();

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const abortControllerRef = useRef(null);
  const tokenRef = useRef(null);

  // اندازه‌گیری main-content-area
  const [containerRect, setContainerRect] = useState(null);
  useEffect(() => {
    const update = () => {
      const mainArea = document.querySelector('.main-content-area');
      if (mainArea) setContainerRect(mainArea.getBoundingClientRect());
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // مخفی کردن Bottom Navigation
  useEffect(() => {
    document.body.classList.add('hide-bottom-nav');
    return () => document.body.classList.remove('hide-bottom-nav');
  }, []);

  // اسکرول خودکار
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinkingSteps]);

  // تنظیم ارتفاع textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 140) + 'px';
  }, [input]);

  const getToken = useCallback(() => {
    try {
      let token = null;
      if (getAuthTokens && typeof getAuthTokens === 'function') {
        const tokens = getAuthTokens();
        if (tokens && tokens.access) token = tokens.access;
        else if (tokens && typeof tokens === 'string') token = tokens;
      }
      if (!token) {
        token = localStorage.getItem('access_token') || localStorage.getItem('token');
      }
      if (token) tokenRef.current = token;
      return token || tokenRef.current;
    } catch {
      return tokenRef.current || null;
    }
  }, [getAuthTokens]);

  const safePersistMessage = useCallback(async (role, content) => {
    try {
      await persistMessage(role, content);
    } catch (e) {
      console.warn('⚠️ Persist failed (non-critical):', e.message);
    }
  }, [persistMessage]);

  // بارگذاری تاریخچه با fetch مستقیم (دور زدن interceptor برای ۴۰۱)
  useEffect(() => {
    if (!isLoggedIn) return;
    const token = getToken();
    if (!token) return;

    (async () => {
      try {
        const sessions = await fetchSessionsWithToken(token);
        if (Array.isArray(sessions) && sessions.length > 0) {
          const lastId = sessions[0].session_id;
          setSessionId(lastId);
          const msgs = await loadSessionMessages(lastId);
          if (msgs.length) {
            setMessages(msgs);
            setChatStarted(true);
          }
        }
      } catch (e) {
        if (e.message === '401') {
          console.warn('🔐 Session token expired – opening auth modal');
          openAuthModal();
        } else {
          console.error('Load history error', e);
        }
      }
    })();
  }, [isLoggedIn, getToken, openAuthModal, setSessionId, loadSessionMessages]);

  const handleSelectSession = async (sid) => {
    setSessionId(sid);
    setChatStarted(true);
    setSessionFull(false);
    const msgs = await loadSessionMessages(sid);
    if (msgs.length) setMessages(msgs);
    else setMessages([]);
  };

  const handleNewChat = () => {
    startNewSession();
    setMessages([]);
    setChatStarted(false);
  };

  // ── ارسال پیام (با بافر خطوط و پردازش باقی‌مانده) ──
  const sendMessage = async (userMessage) => {
    console.log('📨 sendMessage entered with:', userMessage);
    try {
      if (!isLoggedIn) { openAuthModal(); return; }
      const token = getToken();
      if (!token) { openAuthModal(); return; }
      if (!userMessage.trim() || sessionFull) return;

      if (!chatStarted) setChatStarted(true);

      const userMsgObj = { id: Date.now(), role: 'user', content: userMessage.trim() };
      setMessages(prev => [...prev, userMsgObj]);
      safePersistMessage('user', userMessage.trim());

      const assistantMsgId = Date.now() + 1;
      const assistantMsg = {
        id: assistantMsgId,
        role: 'assistant',
        content: '',
        thinkingSteps: [],
        cards: null,
        user_query: userMessage.trim(),
      };
      setMessages(prev => [...prev, assistantMsg]);
      setIsLoading(true);
      setInput('');
      setThinkingSteps([]);

      if (abortControllerRef.current) abortControllerRef.current.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const response = await fetch('https://api.farzaneh.app/api/v1/ai/api/chat/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          search_web: searchWeb,
          deep_search: deepSearch,
        }),
        signal: abortController.signal,
      });

      console.log('📡 Response status:', response.status);

      if (response.status === 400) {
        const errData = await response.json();
        if (errData.error?.includes('حداکثر ۲۰ پرسش')) {
          setSessionFull(true);
          throw new Error('حداکثر ۲۰ پرسش. لطفاً گفتگوی جدید شروع کنید.');
        }
      }

      if (!response.ok) {
        if (response.status === 401) {
          console.warn('⚠️ 401 – opening auth modal');
          openAuthModal();
          return;
        }
        throw new Error(`خطای سرور: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let currentResponseId = null;
      const currentThinking = [];
      let lineBuffer = '';

      // تابع کمکی برای پردازش یک خط
      const processLine = (line) => {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') return;
          try {
            const parsed = JSON.parse(data);

            if (parsed.type === 'cards') {
              const cardsData = parsed.data || [];
              console.log('🎴 Cards received:', cardsData);
              setMessages(prev =>
                prev.map(msg =>
                  msg.id === assistantMsgId
                    ? {
                        ...msg,
                        cards: cardsData.map(c => ({
                          ...c,
                          itemType: c.type === 'book' ? 'book' : 'product',
                        })),
                      }
                    : msg
                )
              );
            } else if (parsed.type === 'meta' && parsed.response_id) {
              currentResponseId = parsed.response_id;
              setMessages(prev =>
                prev.map(msg =>
                  msg.id === assistantMsgId ? { ...msg, response_id: currentResponseId } : msg
                )
              );
            } else if (parsed.type === 'status') {
              currentThinking.push({ content: parsed.content });
              setMessages(prev =>
                prev.map(msg =>
                  msg.id === assistantMsgId
                    ? { ...msg, thinkingSteps: [...currentThinking] }
                    : msg
                )
              );
              setThinkingSteps([...currentThinking]);
            } else if (parsed.text) {
              accumulatedText += parsed.text;
              setMessages(prev =>
                prev.map(msg =>
                  msg.id === assistantMsgId ? { ...msg, content: accumulatedText } : msg
                )
              );
            }
          } catch (e) {
            console.error('❌ JSON parse error for line:', line, e);
          }
        }
      };

      // حلقه خواندن stream
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });

        lineBuffer += chunk;
        const lines = lineBuffer.split('\n');
        lineBuffer = lines.pop() || '';  // نگهداشتن قسمت ناقص
        for (const line of lines) {
          processLine(line);
        }
      }

      // پردازش باقی‌مانده بافر (خطی که ممکن است بدون \n تمام شده باشد)
      if (lineBuffer.trim().length > 0) {
        console.log('🟡 Processing leftover buffer:', lineBuffer);
        const remainingLines = lineBuffer.split('\n');
        for (const line of remainingLines) {
          processLine(line);
        }
      }

      setMessages(prev =>
        prev.map(msg =>
          msg.id === assistantMsgId ? { ...msg, thinkingSteps: currentThinking } : msg
        )
      );

      if (accumulatedText) safePersistMessage('assistant', accumulatedText);

    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('💥 Error in sendMessage:', error);
      setMessages(prev =>
        prev.map(msg =>
          msg.id === Date.now() + 1 ? { ...msg, content: `⚠️ ${error.message}` } : msg
        )
      );
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
      setThinkingSteps([]);
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (input.trim() && !isLoading && !sessionFull) {
      sendMessage(input.trim());
    }
  };

  let portalStyle;
  if (containerRect) {
    portalStyle = {
      position: 'fixed',
      top: containerRect.top,
      left: containerRect.left,
      width: containerRect.width,
      height: containerRect.height,
      zIndex: 40,
      overflow: 'hidden',
    };
  } else {
    return <div style={{ width: '100vw', height: '100vh' }} />;
  }

  return ReactDOM.createPortal(
    <>
      <Helmet>
        <title>دستیار هوشمند | فرزانه</title>
      </Helmet>

      <style>{`
        @keyframes neonPulse {
          0%, 100% { box-shadow: 0 0 8px rgba(172, 138, 220, 0.09), 0 0 20px rgba(96, 165, 250, 0.09); }
          50% { box-shadow: 0 0 15px rgba(125, 58, 164, 0.1), 0 0 30px rgba(96, 165, 250, 0.18); }
        }
        .neon-glow { animation: neonPulse 3s ease-in-out infinite; }
      `}</style>

      <ChatSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onSelectSession={handleSelectSession}
        currentSessionId={sessionId}
        onNewChat={handleNewChat}
      />

      <div style={portalStyle} className="bg-white">
        <div className="flex items-center justify-between h-14 px-4 bg-white/80 backdrop-blur-md border-b border-gray-200 shrink-0">
          <button onClick={() => navigate(-1)} className="p-2">
            <BsArrowRight size={20} />
          </button>
          <h2 className="font-bold text-lg">دستیار فرزانه</h2>
          <button onClick={() => setSidebarOpen(true)} className="p-2">
            <BsList size={20} />
          </button>
        </div>

        <div className="flex flex-col" style={{ height: 'calc(100% - 3.5rem)' }}>
          {!chatStarted ? (
            <WelcomeScreen onSend={sendMessage} />
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-2 py-4">
                {sessionFull && (
                  <div className="text-center text-red-600 bg-red-50 p-2 rounded-lg mb-4">
                    حداکثر ۲۰ پرسش.{' '}
                    <button onClick={handleNewChat} className="underline">
                      گفتگوی جدید
                    </button>
                  </div>
                )}
                <div className="space-y-4">
                  {messages.map(msg => (
                    <ErrorBoundary key={msg.id}>
                      <MessageBubble message={msg} getToken={getToken} />
                    </ErrorBoundary>
                  ))}
                  {isLoading && thinkingSteps.length > 0 && (
                    <div className="flex justify-start">
                      <div className="bg-white/30 backdrop-blur-md rounded-2xl px-4 py-3 border border-white/20 shadow-sm w-fit">
                        <ThinkingSteps steps={thinkingSteps} />
                      </div>
                    </div>
                  )}
                  {isLoading && thinkingSteps.length === 0 && (
                    <div className="flex justify-start">
                      <div className="bg-white/40 backdrop-blur-md rounded-2xl px-4 py-3 border border-white/30 shadow-sm">
                        <span className="text-sm text-gray-600">در حال پردازش...</span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              <div
                className="shrink-0 backdrop-blur-xl bg-white/40 border-t border-white/20 px-3 pt-3"
                style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)', position: 'relative' }}
              >
                <div className="flex flex-col gap-2">
                  <div className="flex items-end gap-2">
                    <textarea
                      ref={textareaRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSubmit(e);
                        }
                      }}
                      placeholder="سوال خود را اینجا بنویسید…"
                      rows={1}
                      className="flex-1 min-h-[48px] max-h-[140px] px-4 py-3 bg-white/30 backdrop-blur-md border border-white/30 rounded-2xl outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 transition-all text-sm resize-none leading-6 placeholder:text-gray-500 text-gray-800"
                      disabled={isLoading || sessionFull}
                    />
                    <button
                      type="button"
                      onClick={() => sendMessage(input.trim())}
                      disabled={!input.trim() || isLoading || sessionFull}
                      className={`relative z-10 h-[48px] w-[48px] rounded-2xl flex items-center justify-center shrink-0 transition-all duration-200 ${
                        input.trim() && !isLoading && !sessionFull
                          ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-200 active:scale-95'
                          : 'bg-gray-200/40 text-gray-400 cursor-not-allowed'
                      }`}
                      aria-label="ارسال پیام"
                    >
                      <BsSend size={17} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between gap-2 px-1">
                    <p className="text-[10px] text-gray-500 shrink-0">
                      Enter ارسال &nbsp;·&nbsp; Shift+Enter خط جدید
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setSearchWeb(p => !p)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-medium backdrop-blur-xl border transition-all duration-200 select-none ${
                          searchWeb
                            ? 'bg-blue-500/20 border-blue-400 text-blue-700 shadow-sm'
                            : 'bg-white/20 border-white/30 text-gray-600 hover:bg-white/30'
                        }`}
                      >
                        <BsSearch size={12} />
                        <span>جستجوی وب</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setDeepSearch(p => !p)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-medium backdrop-blur-xl border transition-all duration-200 select-none ${
                          deepSearch
                            ? 'bg-indigo-500/20 border-indigo-400 text-indigo-700 shadow-sm'
                            : 'bg-white/20 border-white/30 text-gray-600 hover:bg-white/30'
                        }`}
                      >
                        <BsLightbulb size={12} />
                        <span>تفکر عمیق</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}